import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Calendar, Folder, Archive, Tag } from 'lucide-react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import BlogCard from '@/components/shared/BlogCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { publicApi } from '@/lib/api';

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [archives, setArchives] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const currentCategory = searchParams.get('category');
  const currentSearch = searchParams.get('search');
  const currentPage = parseInt(searchParams.get('page') || '1');
  const postsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          limit: postsPerPage,
          skip: (currentPage - 1) * postsPerPage,
        };
        if (currentCategory) params.category = currentCategory;
        if (currentSearch) params.search = currentSearch;

        const [postsRes, countRes, recentRes, categoriesRes, archivesRes] = await Promise.all([
          publicApi.getBlogPosts(params),
          publicApi.getBlogCount({ category: currentCategory, search: currentSearch }),
          publicApi.getRecentPosts(5),
          publicApi.getCategories(),
          publicApi.getBlogArchives()
        ]);

        setPosts(postsRes.data);
        setTotalCount(countRes.data.count);
        setRecentPosts(recentRes.data);
        setCategories(categoriesRes.data);
        setArchives(archivesRes.data);
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentCategory, currentSearch, currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const handleCategoryClick = (slug) => {
    if (slug) {
      setSearchParams({ category: slug });
    } else {
      setSearchParams({});
    }
  };

  const totalPages = Math.ceil(totalCount / postsPerPage);

  const formatArchiveDate = (dateString) => {
    const [year, month] = dateString.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags for Blog Listing */}
      <Helmet>
        <title>Career Development Blog | SEG Career Support</title>
        <meta name="description" content="Expert career advice, job search strategies, resume tips, interview preparation, and professional development insights from SEG Career Support. Advance your career with proven strategies." />
        <meta name="keywords" content="career advice, job search tips, resume writing, interview preparation, career development, professional growth, networking, salary negotiation" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/blog`} />
        <meta property="og:title" content="Career Development Blog | SEG Career Support" />
        <meta property="og:description" content="Expert career advice, job search strategies, resume tips, interview preparation, and professional development insights." />
        <meta property="og:site_name" content="SEG Career Support" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Career Development Blog | SEG Career Support" />
        <meta name="twitter:description" content="Expert career advice, job search strategies, resume tips, interview preparation, and professional development insights." />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`${window.location.origin}/blog`} />
      </Helmet>

      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="Blog" 
          breadcrumbs={[{ label: 'Blog' }]} 
        />

        <section className="py-16" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12">
                    <h3 className="text-xl font-heading font-bold mb-2">No posts found</h3>
                    <p className="text-muted-foreground">Try a different search or category.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid md:grid-cols-2 gap-8">
                      {posts.map((post) => (
                        <BlogCard key={post.id} post={post} />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center gap-2 mt-12">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={page === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSearchParams(prev => {
                              const params = new URLSearchParams(prev);
                              params.set('page', page.toString());
                              return params;
                            })}
                            data-testid={`pagination-${page}`}
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-8">
                {/* Search */}
                <Card data-testid="blog-search-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="w-5 h-5" /> Search
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSearch} className="flex gap-2">
                      <Input
                        placeholder="Search posts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="blog-search-input"
                      />
                      <Button type="submit" size="icon" data-testid="blog-search-btn">
                        <Search className="w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Recent Posts */}
                <Card data-testid="recent-posts-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" /> Recent Posts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      {recentPosts.map((post) => (
                        <li key={post.id}>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="text-sm hover:text-primary transition-colors line-clamp-2"
                            data-testid={`recent-post-${post.slug}`}
                          >
                            {post.title}
                          </Link>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Categories */}
                <Card data-testid="categories-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Folder className="w-5 h-5" /> Categories
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      <li>
                        <button
                          onClick={() => handleCategoryClick(null)}
                          className={`text-sm hover:text-primary transition-colors ${!currentCategory ? 'text-primary font-medium' : ''}`}
                          data-testid="category-all"
                        >
                          All Posts
                        </button>
                      </li>
                      {categories.map((category) => (
                        <li key={category.id}>
                          <button
                            onClick={() => handleCategoryClick(category.slug)}
                            className={`text-sm hover:text-primary transition-colors flex justify-between w-full ${currentCategory === category.slug ? 'text-primary font-medium' : ''}`}
                            data-testid={`category-${category.slug}`}
                          >
                            <span>{category.name}</span>
                            <span className="text-muted-foreground">({category.post_count})</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Archives */}
                {archives.length > 0 && (
                  <Card data-testid="archives-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Archive className="w-5 h-5" /> Archives
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {archives.slice(0, 6).map((archive) => (
                          <li key={archive.date}>
                            <span className="text-sm text-muted-foreground">
                              {formatArchiveDate(archive.date)} ({archive.count})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
